interface JsonLdProps {
  data: Record<string, unknown>;
}

const BASE = "https://www.naklejki-3d.com.pl";

const JsonLd = ({ data }: JsonLdProps) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
  />
);

// Organization schema for homepage
export const OrganizationJsonLd = () => (
  <JsonLd
    data={{
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${BASE}/#organization`,
      name: "Naklejki-3d.com.pl",
      alternateName: "Wypukle.pl",
      url: BASE,
      logo: {
        "@type": "ImageObject",
        url: `${BASE}/favicon.png`,
        width: 512,
        height: 512,
      },
      image: `${BASE}/favicon.png`,
      description:
        "Producent naklejek 3D wypukłych zalewanych żywicą poliuretanową. Dowolny kształt, rozmiar i nakład.",
      telephone: "+48534900620",
      email: "biuro@projekt-art.pl",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Katowice",
        addressCountry: "PL",
      },
      sameAs: ["https://www.instagram.com/wypukle.pl/"],
      knowsAbout: [
        "naklejki 3D", "doming", "naklejki wypukłe", "naklejki żywiczne",
        "naklejki epoksydowe", "naklejki poliuretanowe", "naklejki domingowe",
        "naklejki z logo", "etykiety 3D", "logo wypukłe",
      ],
    }}
  />
);

// Product schema for products page
export const ProductJsonLd = () => (
  <JsonLd
    data={{
      "@context": "https://schema.org",
      "@type": "Product",
      "@id": `${BASE}/produkty/#product`,
      name: "Naklejki 3D Wypukłe Zalewane Żywicą (Doming)",
      description:
        "Naklejki wypukłe 3D zalewane żywicą poliuretanową – trwałe ponad 10 lat, odporne na UV, wodę, chemikalia i zarysowania. Naklejki domingowe w dowolnym kształcie i wymiarze z opcją złocenia i srebrzenia.",
      image: [`${BASE}/favicon.png`],
      url: `${BASE}/produkty`,
      sku: "DOMING-3D",
      mpn: "NAKLEJKI-3D-DOMING",
      brand: { "@type": "Brand", name: "Naklejki-3d.com.pl" },
      manufacturer: { "@type": "Organization", name: "DRUKEXPERT.PL Sp. z o.o." },
      category: "Naklejki reklamowe 3D",
      additionalProperty: [
        { "@type": "PropertyValue", name: "Wysokość wypukłości", value: "około 2 mm" },
        { "@type": "PropertyValue", name: "Trwałość", value: "ponad 10 lat" },
        { "@type": "PropertyValue", name: "Odporność na UV", value: "Tak" },
        { "@type": "PropertyValue", name: "Żywica", value: "poliuretanowa dwuskładnikowa" },
      ],
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "PLN",
        lowPrice: "0.50",
        highPrice: "50.00",
        availability: "https://schema.org/InStock",
        offerCount: "15",
        url: `${BASE}/kalkulator`,
        seller: {
          "@type": "Organization",
          name: "Naklejki-3d.com.pl",
          url: BASE,
        },
      },
    }}
  />
);

// Article schema for blog posts
export const ArticleJsonLd = ({
  title,
  description,
  date,
  slug,
  basePath = "/blog",
}: {
  title: string;
  description: string;
  date: string;
  slug: string;
  basePath?: string;
}) => {
  const url = `${BASE}${basePath}/${slug}/`;
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "Article",
        headline: title,
        description,
        datePublished: date,
        dateModified: date,
        url,
        author: { "@type": "Organization", name: "Naklejki-3d.com.pl" },
        publisher: {
          "@type": "Organization",
          name: "Naklejki-3d.com.pl",
          logo: { "@type": "ImageObject", url: `${BASE}/favicon.png` },
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
      }}
    />
  );
};

// FAQPage schema
export const FaqJsonLd = ({ faqs }: { faqs: { q: string; a: string }[] }) => (
  <JsonLd
    data={{
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    }}
  />
);

// LocalBusiness schema
export const LocalBusinessJsonLd = () => (
  <JsonLd
    data={{
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${BASE}/#localbusiness`,
      name: "Naklejki 3D – Producent Naklejek Doming",
      description:
        "Producent naklejek 3D wypukłych (doming) dla firm. Naklejki żywiczne na zamówienie B2B.",
      url: BASE,
      image: `${BASE}/favicon.png`,
      telephone: "+48534900620",
      email: "biuro@projekt-art.pl",
      address: { "@type": "PostalAddress", addressLocality: "Katowice", addressCountry: "PL" },
      areaServed: ["PL", "DE", "FR", "EU"],
      serviceType: "Druk naklejek 3D wypukłych",
      priceRange: "$$",
      openingHoursSpecification: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "17:00",
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Naklejki 3D Doming",
        itemListElement: [
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Naklejki wypukłe 3D zalewane żywicą poliuretanową" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Naklejki domingowe z logo firmy" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Etykiety 3D odporne na UV i chemikalia" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Naklejki holograficzne, złocone, srebrzone premium" } },
        ],
      },
    }}
  />
);

export default JsonLd;
